import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Flex,
  HStack,
  Box,
  Text,
  Heading,
  IconButton,
  Tooltip,
  InputGroup,
  InputLeftElement,
  Input,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  Badge,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  useColorModeValue,
  useColorMode,
  Button,
  Divider,
  VStack,
  useDisclosure,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Spinner,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
} from '@chakra-ui/react';
import {
  FaBars,
  FaSearch,
  FaPlus,
  FaBell,
  FaRobot,
  FaUserCircle,
  FaSignOutAlt,
  FaSun,
  FaMoon,
  FaExternalLinkAlt,
} from 'react-icons/fa';
import ConsorcioSwitcher from './ConsorcioSwitcher';
import NotificationsCenter from './NotificationsCenter';
import NovedadesWamBoton from './NovedadesWam/NovedadesWamBoton';
import { SupportBotDrawerPanel } from '../Private/_Dashboard/SupportBotWidget';
import WamAutopilotNotificationBadge from './Wam/WamAutopilotNotificationBadge';
import { downloadFile } from '../../_Utils/mobileBridge';
import { getStoredAuthHeaders } from '../../_Utils/authHeaders';
import AsistenteBoton from './Asistente/AsistenteBoton';
import { useAsistente } from './Asistente/AsistenteContext';

const DEFAULT_QUICK_ACTIONS = [
  { label: 'Nuevo Gasto', path: '/panel/movimientos/gastos/agregar/gasto' },
  { label: 'Nueva Cobranza', path: '/panel/movimientos/cobranzas/agregar/cobranza' },
  { label: 'Nuevo Reclamo', path: '/panel/servicios/reclamos' },
  { label: 'Nueva Liquidación', path: '/panel/liquidacion/agregar/liquidacion-expensa' },
];

const HeaderV2 = ({
  drawerDisclosure,
  title = 'Panel',
  parentTitle = null,
  breadcrumbs = [],
  unreadCount = 0,
  onOpenNotifications,
  onNavigate,
  onLogout,
  user,
  searchQuery = '',
  onSearchQueryChange,
  searchResults = null,
  searchLoading = false,
  searchError = null,
  onSearchSubmit,
  onSearchSelect,
  onSearchClear,
  headerHeight = 64,
  periodStatus = null,
  quickActions = DEFAULT_QUICK_ACTIONS,
  notifications = [],
  notificationCounts = {},
  jobStatus = null,
  weatherMeta = null,
  quickBalance = null,
  jobs = [],
  onConsorcioChange,
  onConsorciosLoaded,
  selectedConsorcio = null,
  currentPath = '/',
  onRefreshNotifications,
  contextSelectorNode = null,
}) => {
  const [now, setNow] = useState(new Date());
  const aiDrawer = useDisclosure();
  // Con el chat nuevo habilitado (allowlist) se oculta el botón "Asistente IA" anterior.
  const { habilitado: chatMcpHabilitado } = useAsistente();
  const searchModal = useDisclosure();
  const notificationsPanel = useDisclosure();
  const jobDrawer = useDisclosure();
  const saldoDrawer = useDisclosure();
  const { colorMode, toggleColorMode } = useColorMode();
  const headerV2ProFlag = process.env.REACT_APP_HEADER_V2_PRO;
  const isPro = headerV2ProFlag === undefined ? true : headerV2ProFlag === 'true';
  const assistantEnv = process.env.REACT_APP_ASSISTANT_IN_HEADER;
  const assistantEnabled =
    assistantEnv !== undefined
      ? assistantEnv === 'true'
      : process.env.REACT_APP_HEADER_V2 === 'true' || isPro;
  const searchPopover = useDisclosure();

  const headerBg = useColorModeValue('rgba(255,255,255,0.88)', 'rgba(15,23,42,0.78)');
  const borderColor = useColorModeValue('rgba(226,232,240,0.95)', 'rgba(255,255,255,0.12)');
  const headerShadow = useColorModeValue('0 14px 34px rgba(15, 23, 42, 0.08)', '0 10px 30px rgba(2, 6, 23, 0.45)');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const subtleText = useColorModeValue('gray.600', 'gray.300');
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const chipBg = useColorModeValue('rgba(241,245,249,0.92)', 'rgba(255,255,255,0.08)');
  const chipHover = useColorModeValue('rgba(226,232,240,0.96)', 'rgba(255,255,255,0.14)');
  const surfaceBg = useColorModeValue('white', 'gray.800');
  const surfaceBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
  const searchBg = useColorModeValue('rgba(248,250,252,0.95)', 'rgba(15,23,42,0.86)');
  const envLabel =
    process.env.REACT_APP_ENV_LABEL ||
    (process.env.NODE_ENV === 'production'
      ? 'PROD'
      : process.env.NODE_ENV === 'staging'
      ? 'STG'
      : 'DEV');
  const envColor =
    envLabel === 'PROD' ? 'red' : envLabel === 'STG' ? 'orange' : 'green';

  const crumbs = useMemo(() => {
    const base = parentTitle ? [parentTitle] : [];
    return [...base, ...breadcrumbs.filter(Boolean)].filter(Boolean);
  }, [parentTitle, breadcrumbs]);

  const quickActionsMemo = useMemo(
    () => (quickActions?.length ? quickActions : DEFAULT_QUICK_ACTIONS),
    [quickActions]
  );

  const assistantContext = useMemo(() => {
    if (!assistantEnabled) return null;
    return {
      path: currentPath,
      title,
      consorcio_id: selectedConsorcio?.id || null,
      consorcio:
        selectedConsorcio?.domicilio ||
        selectedConsorcio?.nombre ||
        null,
    };
  }, [assistantEnabled, currentPath, title, selectedConsorcio]);

  const timeLabel = now.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
  const weatherLabel =
    weatherMeta && weatherMeta.temperature != null ? `${weatherMeta.temperature}°C` : 'Clima';
  const saldoLabel = quickBalance
    ? `$ ${Number(quickBalance.fondo_operativo || quickBalance.saldo_banco || 0).toLocaleString('es-AR')}`
    : 'Saldo';

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const trimmedSearch = (searchQuery || '').trim();
  const rawSearchGroups = useMemo(() => {
    if (!searchResults) return {};
    if (Array.isArray(searchResults)) {
      return { resultados: searchResults };
    }
    return searchResults.groups || searchResults;
  }, [searchResults]);

  const searchGroups = useMemo(() => {
    const preferredOrder = [
      'consorcios',
      'propietarios',
      'proveedores',
      'gastos',
      'cobranzas',
      'reclamos',
      'liquidaciones',
    ];
    const ordered = [];
    preferredOrder.forEach((key) => {
      const items = rawSearchGroups[key];
      if (Array.isArray(items) && items.length > 0) {
        ordered.push({ key, label: SEARCH_GROUP_LABELS[key] || key, items });
      }
    });
    Object.entries(rawSearchGroups).forEach(([key, items]) => {
      if (preferredOrder.includes(key)) return;
      if (Array.isArray(items) && items.length > 0) {
        ordered.push({ key, label: SEARCH_GROUP_LABELS[key] || key, items });
      }
    });
    return ordered;
  }, [rawSearchGroups]);

  const flatSearchResults = useMemo(
    () => searchGroups.flatMap((group) => group.items || []),
    [searchGroups]
  );

  const handleResultSelect = useCallback(
    (url) => {
      if (!url) return;
      onSearchSelect?.(url);
      onSearchClear?.();
      searchPopover.onClose();
      searchModal.onClose();
    },
    [onSearchClear, onSearchSelect, searchPopover, searchModal]
  );

  const handleSearchSubmit = useCallback(() => {
    if (!trimmedSearch || flatSearchResults.length === 0) return;
    handleResultSelect(flatSearchResults[0]?.url);
  }, [flatSearchResults, handleResultSelect, trimmedSearch]);

  const handleSearchKey = (ev) => {
                    if (ev.key === 'Enter') {
                      ev.preventDefault();
                      if (trimmedSearch) {
                        onSearchSubmit?.();
                        searchPopover.onOpen();
                      }
                      handleSearchSubmit();
                    }
    if (ev.key === 'Escape') {
      ev.preventDefault();
      onSearchQueryChange?.('');
      onSearchClear?.();
      searchPopover.onClose();
      ev.currentTarget.blur();
    }
  };

  const handleNotificationsClick = () => {
    if (isPro) notificationsPanel.onOpen();
    else onOpenNotifications?.();
  };

  useEffect(() => {
    if (trimmedSearch.length >= 2) {
      searchPopover.onOpen();
    } else {
      searchPopover.onClose();
    }
  }, [trimmedSearch, searchPopover]);

  return (
    <>
      <Flex
        as="header"
        position="fixed"
        top={0}
        left={0}
        right={0}
        h={`${headerHeight}px`}
        px={{ base: 3, md: 6 }}
        align="center"
        justify="space-between"
        bg={headerBg}
        color={textColor}
        borderBottom="1px solid"
        borderColor={borderColor}
        backdropFilter="blur(10px)"
        boxShadow={headerShadow}
        zIndex={220}
        minW={0}
      >
        <HStack spacing={{ base: 2, md: 3 }} flex="1" minW={0} overflow="hidden">
          <IconButton
            aria-label="Abrir menú"
            icon={<FaBars />}
            display={{ base: 'inline-flex', md: 'none' }}
            onClick={drawerDisclosure?.onOpen}
            variant="ghost"
            flexShrink={0}
          />
          <Box minW={0} maxW={{ base: '160px', md: '260px', lg: '340px' }}>
            {crumbs.length > 0 && (
              <Text fontSize="xs" color={mutedText} noOfLines={1}>
                {crumbs.join(' / ')}
              </Text>
            )}
            <Heading size="sm" noOfLines={1}>
              {title}
            </Heading>
          </Box>
          {contextSelectorNode && (
            <Box display={{ base: 'none', xl: 'block' }} flexShrink={0}>{contextSelectorNode}</Box>
          )}
          {isPro && (
            <Box flexShrink={0}>
              <ConsorcioSwitcher
                onChange={onConsorcioChange}
                value={selectedConsorcio?.id ?? null}
                onListaCargada={onConsorciosLoaded}
              />
            </Box>
          )}
        </HStack>

        <Box
          flex="1"
          minW={0}
          px={{ base: 0, lg: 4, xl: 6 }}
          maxW={{ lg: '420px', xl: '600px' }}
          display={{ base: 'none', xl: 'block' }}
        >
          <Popover
            isOpen={searchPopover.isOpen && trimmedSearch.length >= 2}
            onClose={searchPopover.onClose}
            placement="bottom"
            closeOnBlur={false}
            autoFocus={false}
            returnFocusOnClose={false}
            trapFocus={false}
            matchWidth
          >
            <PopoverTrigger>
              <Box onFocusCapture={() => searchPopover.onOpen()}>
                <InputGroup>
                  <InputLeftElement pointerEvents="none" color={mutedText}>
                    <FaSearch />
                  </InputLeftElement>
                  <Input
                    placeholder="Buscar propietario, UF, reclamo, factura..."
                    value={searchQuery}
                    onFocus={() => searchPopover.onOpen()}
                    onChange={(e) => onSearchQueryChange?.(e.target.value)}
                    onKeyDown={handleSearchKey}
                    borderRadius="22px"
                    bg={searchBg}
                    borderColor={surfaceBorder}
                    boxShadow="inset 0 1px 2px rgba(15, 23, 42, 0.05)"
                    _placeholder={{ color: mutedText }}
                  />
                </InputGroup>
              </Box>
            </PopoverTrigger>
            <PopoverContent
              mt={2}
              bg={surfaceBg}
              borderColor={surfaceBorder}
              shadow="lg"
              maxH="420px"
              overflow="hidden"
            >
              <PopoverArrow />
              <PopoverBody>
                <SearchResultsList
                  groups={searchGroups}
                  loading={searchLoading && trimmedSearch.length >= 2}
                  error={searchError}
                  onSelect={handleResultSelect}
                  emptyLabel="Sin resultados"
                />
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </Box>

        <HStack spacing={{ base: 1, md: 2 }} flexShrink={0} pl={{ base: 1, md: 2 }}>
          {isPro && (
            <>
              <Button
                size="sm"
                variant="ghost"
                borderRadius="full"
                bg={chipBg}
                _hover={{ bg: chipHover }}
                color={subtleText}
                fontWeight="500"
                display={{ base: 'none', xl: 'inline-flex' }}
              >
                {timeLabel}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                borderRadius="full"
                bg={chipBg}
                _hover={{ bg: chipHover }}
                color={subtleText}
                fontWeight="500"
                display={{ base: 'none', xl: 'inline-flex' }}
              >
                {weatherLabel}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                borderRadius="full"
                bg={chipBg}
                _hover={{ bg: chipHover }}
                color={subtleText}
                onClick={saldoDrawer.onOpen}
                isDisabled={!quickBalance}
                display={{ base: 'none', xl: 'inline-flex' }}
              >
                {saldoLabel}
              </Button>
              {jobs.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  borderRadius="full"
                  onClick={jobDrawer.onOpen}
                  display={{ base: 'none', '2xl': 'inline-flex' }}
                >
                  {jobs[0].label} · {Math.round(jobs[0].percent || 0)}%
                </Button>
              )}
            </>
          )}
          <Tooltip hasArrow label="Buscar">
            <IconButton
              aria-label="Buscar"
              icon={<FaSearch />}
              variant="ghost"
              borderRadius="full"
              display={{ base: 'inline-flex', xl: 'none' }}
              onClick={searchModal.onOpen}
              flexShrink={0}
            />
          </Tooltip>

         <Menu placement="bottom-end">
            <Tooltip hasArrow label="Acciones rápidas">
              <MenuButton
                as={IconButton}
                icon={<FaPlus />}
                aria-label="Acciones rápidas"
                variant="ghost"
                borderRadius="full"
                flexShrink={0}
              />
            </Tooltip>
            <MenuList bg={surfaceBg} borderColor={surfaceBorder}>
              {quickActionsMemo.map((action) => (
                <MenuItem
                  key={action.label}
                  onClick={() => onNavigate?.(action.path)}
                >
                  {action.label}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>

          <AsistenteBoton />

          <NovedadesWamBoton />

          <Box position="relative">
            <Tooltip hasArrow label="Notificaciones">
              <IconButton
                aria-label="Notificaciones"
                icon={<FaBell />}
                variant="ghost"
                borderRadius="full"
                onClick={handleNotificationsClick}
              />
            </Tooltip>
            {unreadCount > 0 && (
              <Badge
                colorScheme="red"
                borderRadius="full"
                position="absolute"
                top="-2px"
                right="-2px"
                fontSize="0.6rem"
                px={1}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </Badge>
            )}
          </Box>

          <Box display={{ base: 'none', lg: 'block' }} flexShrink={0}>
            <WamAutopilotNotificationBadge />
          </Box>

          {periodStatus && (
            <Badge colorScheme="purple" borderRadius="full" display={{ base: 'none', xl: 'inline-flex' }}>
              {periodStatus}
            </Badge>
          )}

          {jobStatus && (
            <Badge colorScheme="orange" borderRadius="full" display={{ base: 'none', xl: 'inline-flex' }}>
              {jobStatus}
            </Badge>
          )}

          {!chatMcpHabilitado && (
            <Tooltip hasArrow label="Asistente IA">
              <IconButton
                aria-label="Asistente IA"
                icon={<FaRobot />}
                variant="ghost"
                borderRadius="full"
                onClick={aiDrawer.onOpen}
                display={{ base: 'none', lg: 'inline-flex' }}
                flexShrink={0}
              />
            </Tooltip>
          )}

          <Tooltip
            hasArrow
            label={colorMode === 'light' ? 'Activar modo oscuro' : 'Desactivar modo oscuro'}
          >
            <IconButton
              aria-label="Alternar modo de color"
              icon={colorMode === 'light' ? <FaMoon /> : <FaSun />}
              variant="ghost"
              borderRadius="full"
              onClick={toggleColorMode}
              flexShrink={0}
            />
          </Tooltip>

          <Menu placement="bottom-end">
            <MenuButton
              as={Button}
              variant="ghost"
              borderRadius="full"
              px={{ base: 1, md: 2 }}
              py={1}
              minW="auto"
              flexShrink={0}
              aria-label="Menú de usuario"
            >
              <HStack spacing={2} minW={0}>
                <Avatar
                  size="sm"
                  name={`${user?.first_name || ''} ${user?.last_name || ''}`}
                />
                <Box display={{ base: 'none', lg: 'block' }} textAlign="left" minW={0} maxW="180px">
                  <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                    {user?.first_name ? `${user.first_name} ${user.last_name}` : user?.username || 'Usuario'}
                  </Text>
                  <Text fontSize="xs" color={mutedText} noOfLines={1}>
                    {user?.email || ''}
                  </Text>
                </Box>
              </HStack>
            </MenuButton>
            <MenuList bg={surfaceBg} borderColor={surfaceBorder}>
              <MenuItem icon={<FaUserCircle />}>Perfil</MenuItem>
              <MenuItem
                icon={<FaSignOutAlt />}
                color="red.400"
                onClick={onLogout}
              >
                Cerrar sesión
              </MenuItem>
            </MenuList>
          </Menu>

          {isPro && (
            <Badge colorScheme={envColor} borderRadius="full" display={{ base: 'none', xl: 'inline-flex' }}>
              {envLabel}
            </Badge>
          )}
        </HStack>
      </Flex>

      <Modal isOpen={searchModal.isOpen} onClose={searchModal.onClose} size="md">
        <ModalOverlay />
        <ModalContent bg={surfaceBg}>
          <ModalHeader>Búsqueda global</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <InputGroup>
              <InputLeftElement pointerEvents="none" color={mutedText}>
                <FaSearch />
              </InputLeftElement>
              <Input
                placeholder="Buscar propietario, UF, reclamo, factura..."
                value={searchQuery}
                onChange={(e) => onSearchQueryChange?.(e.target.value)}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter') {
                    ev.preventDefault();
                    if (trimmedSearch) {
                      onSearchSubmit?.();
                    }
                    handleSearchSubmit();
                  }
                  if (ev.key === 'Escape') {
                    ev.preventDefault();
                    onSearchQueryChange?.('');
                    onSearchClear?.();
                    searchModal.onClose();
                  }
                }}
                borderRadius="22px"
                bg={searchBg}
                borderColor={surfaceBorder}
                boxShadow="inset 0 1px 2px rgba(15, 23, 42, 0.05)"
                _placeholder={{ color: mutedText }}
                autoFocus
              />
            </InputGroup>
            <Box mt={4}>
              <SearchResultsList
                groups={searchGroups}
                loading={searchLoading && trimmedSearch.length >= 2}
                error={searchError}
                onSelect={(url) => {
                  handleResultSelect(url);
                  searchModal.onClose();
                }}
                emptyLabel="Sin resultados"
              />
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

      <NotificationsCenter
        isOpen={notificationsPanel.isOpen && isPro}
        onClose={notificationsPanel.onClose}
        notifications={notifications}
        counts={notificationCounts}
        onRefresh={onRefreshNotifications}
      />

      <Drawer isOpen={aiDrawer.isOpen} placement="right" onClose={aiDrawer.onClose} size="sm">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerBody px={0} py={0}>
            {assistantEnabled ? (
              <SupportBotDrawerPanel
                onClose={aiDrawer.onClose}
                contextMeta={assistantContext}
              />
            ) : (
              <VStack align="stretch" spacing={4} p={6}>
                <Text color={mutedText}>
                  Próximamente podrás conversar con tu asistente para generar reportes, buscar datos y crear tareas sin salir del panel.
                </Text>
                <Divider />
                <Button onClick={aiDrawer.onClose} alignSelf="flex-start">
                  Cerrar
                </Button>
              </VStack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <JobsDrawer isOpen={jobDrawer.isOpen} onClose={jobDrawer.onClose} jobs={jobs} />
      <SaldoDrawer
        isOpen={saldoDrawer.isOpen}
        onClose={saldoDrawer.onClose}
        quickBalance={quickBalance}
      />
    </>
  );
};

export default HeaderV2;

const SEARCH_GROUP_LABELS = {
  consorcios: 'Consorcios',
  propietarios: 'Propietarios',
  proveedores: 'Proveedores',
  gastos: 'Gastos',
  cobranzas: 'Cobranzas',
  reclamos: 'Reclamos',
  liquidaciones: 'Liquidaciones',
  resultados: 'Resultados',
};

const SearchResultsList = ({ groups = [], loading, error, onSelect, emptyLabel = 'Sin resultados' }) => {
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');

  if (loading) {
    return (
      <HStack py={4} spacing={3} justify="center">
        <Spinner size="sm" />
        <Text fontSize="sm" color={mutedText}>
          Buscando…
        </Text>
      </HStack>
    );
  }

  if (error) {
    return (
      <Box py={3}>
        <Text fontSize="sm" color="red.400">
          {error}
        </Text>
      </Box>
    );
  }

  if (!groups || groups.length === 0) {
    return (
      <Box py={3}>
        <Text fontSize="sm" color={mutedText}>
          {emptyLabel}
        </Text>
      </Box>
    );
  }

  return (
    <VStack align="stretch" spacing={3} maxH="360px" overflowY="auto" pr={1}>
      {groups.map((group) => (
        <Box key={group.key}>
          <Text fontSize="xs" textTransform="uppercase" color={mutedText} mb={1}>
            {group.label || SEARCH_GROUP_LABELS[group.key] || group.key}
          </Text>
          <VStack align="stretch" spacing={1}>
            {group.items.map((item) => (
              <Flex
                key={`${group.key}-${item.id || item.url || item.title}`}
                p={2}
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="md"
                align="flex-start"
                cursor="pointer"
                role="button"
                _hover={{ bg: hoverBg }}
                onClick={() => onSelect?.(item.url)}
              >
                <Box flex="1">
                  <Text fontWeight="medium" fontSize="sm">
                    {item.title || item.name || 'Sin título'}
                  </Text>
                  {!!item.subtitle && (
                    <Text fontSize="xs" color={mutedText}>
                      {item.subtitle}
                    </Text>
                  )}
                </Box>
                <Icon as={FaExternalLinkAlt} ml={2} mt={1} fontSize="xs" color={mutedText} />
              </Flex>
            ))}
          </VStack>
        </Box>
      ))}
    </VStack>
  );
};

const JobsDrawer = ({ isOpen, onClose, jobs = [] }) => {
  const surfaceBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const handleResultClick = useCallback((job) => {
    if (!job?.result_url) return;
    const headers = getStoredAuthHeaders() || undefined;
    downloadFile({
      url: job.result_url,
      filename: job.result_filename || undefined,
      headers,
    });
  }, []);
  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="sm">
      <DrawerOverlay />
      <DrawerContent bg={surfaceBg}>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" borderColor={borderColor}>
          Procesos en curso
        </DrawerHeader>
        <DrawerBody>
          {jobs.length === 0 ? (
            <Text color={mutedText}>Sin tareas activas.</Text>
          ) : (
            <VStack align="stretch" spacing={3}>
              {jobs.map((job) => (
                <Box key={job.task_id} borderWidth="1px" borderRadius="lg" p={3} borderColor={borderColor}>
                  <Text fontWeight="bold">{job.label}</Text>
                  <Text fontSize="sm" color={mutedText}>
                    {job.status} · {Math.round(job.percent || 0)}%
                  </Text>
                  {job.result_url && (
                    <Button
                      size="xs"
                      mt={2}
                      variant="link"
                      onClick={() => handleResultClick(job)}
                    >
                      Ver resultado
                    </Button>
                  )}
                </Box>
              ))}
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

const SaldoDrawer = ({ isOpen, onClose, quickBalance }) => {
  const surfaceBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="sm">
      <DrawerOverlay />
      <DrawerContent bg={surfaceBg}>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" borderColor={borderColor}>
          Saldo del consorcio
        </DrawerHeader>
        <DrawerBody>
          {quickBalance ? (
            <VStack align="stretch" spacing={2}>
              <Text fontWeight="bold">{quickBalance.consorcio?.domicilio}</Text>
              <Text>
                Saldo bancos: $
                {Number(quickBalance.saldo_banco || 0).toLocaleString('es-AR')}
              </Text>
              <Text>
                Saldo cajas: $
                {Number(quickBalance.saldo_caja || 0).toLocaleString('es-AR')}
              </Text>
              <Text fontWeight="bold">
                Fondo operativo: $
                {Number(quickBalance.fondo_operativo || 0).toLocaleString('es-AR')}
              </Text>
              {quickBalance.updated_at && (
                <Text fontSize="xs" color={mutedText}>
                  Actualizado: {new Date(quickBalance.updated_at).toLocaleString()}
                </Text>
              )}
            </VStack>
          ) : (
            <Text color={mutedText}>Seleccioná un consorcio para ver el saldo.</Text>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
